import { Meta, StoryFn } from "@storybook/react";
import type { Message } from "@langchain/langgraph-sdk";
import { action } from "@storybook/addon-actions";

import { LangGraphBot } from "@/Apps/AITools/LangGraphBot/LangGraphBot";
import {
  NewspaperClipping,
  ThumbsDown,
  ThumbsUp,
  TreeStructure,
} from "@phosphor-icons/react";
import { Browsers } from "@phosphor-icons/react";
import { ChangedComponentTool } from "./components/ChangedComponentTool";
import { PageBuilderArtifact } from "./components/PageBuilderArtifact";

import { messagesWithToolCalls, messagesWithPageContent } from "./data";
import { LangGraphBotProps } from "./types";
import { CampaignBuilderArtifact } from "./components/CampaignBuilderArtifact";
import { useState, useRef } from "react";

import PureCampaignBuilder from "@/Apps/Funnels/CampaignBuilder/PureCampaignBuilder";
import { generatedObjects } from "@/Apps/Funnels/CampaignBuilder/Nodes/sample";
import Button from "@/ui/Button";

import "@/Apps/Pages/Page/layout.css";

import { ASSISTANT_IDS } from "../env";
import { LANGGRAPH_DOMAIN } from "@launchos/shared-utils/env";
import { FeedbackButtonType } from "@/ui/CommunicationView/ChatConversation/types";
import { SwitchGroup } from "@/Apps/Pages/Properties/components/groups/SwitchGroup/SwitchGroup";
import { StatusBar } from "./StatusBar";

export const cleanFromLangGraph = (content: any) => {
  if (content?.pageContent) {
    return content.pageContent;
  }
  return content;
};

export default {
  title: "Components/AI Tools/LangGraph Bot",
  component: LangGraphBot,
  argTypes: {
    onUpdate: { action: "updated" },
    onFinish: { action: "finished" },
  },
  //   tags: ["autodocs"],
} as Meta;

const Template: StoryFn<LangGraphBotProps> = (args) => {
  return (
    <div className="h-full w-full">
      <LangGraphBot {...args} />
    </div>
  );
};

const artifacts = [
  {
    id: "add_component",
    title: "Add a component",
    component: ChangedComponentTool,
  },
  {
    id: "modify_component",
    title: "Modify a component",
    component: ChangedComponentTool,
  },
  {
    id: "delete_component",
    title: "Delete a component",
    component: ChangedComponentTool,
  },
  {
    id: "page_builder",
    title: "Page Builder",
    component: PageBuilderArtifact,
  },
  {
    id: "campaign_builder",
    title: "Campaign Builder",
    component: CampaignBuilderArtifact,
  },
];

export const feedbackButtons: FeedbackButtonType[] = [
  {
    // label: "Yes",
    onClick: (id: string, caption: string) => {
      // console.log("feedback button (thumbs up) clicked", id, caption);
      const response = window.prompt(
        "Please share a brief description of why you liked this response"
      );
      if (response) {
        console.log("response", response, id, caption);
      }
    },
    icon: (
      <ThumbsUp
        size={24}
        className="p-1 dark:text-white/50 text-black/50 dark:hover:text-white hover:text-black transition-colors duration-200"
      />
    ),
    // tooltip: "Yes",
  },
  {
    // label: "No",
    onClick: (id: string, caption: string) => {
      // console.log("feedback button (thumbs down) clicked", id, caption);
      const response = window.prompt(
        "Please share a brief description of why you didn't like this response"
      );
      if (response) {
        console.log("response", response, id, caption);
      }
    },
    icon: (
      <ThumbsDown
        size={24}
        className="p-1 dark:text-white/50 text-black/50 dark:hover:text-white hover:text-black transition-colors duration-200"
      />
    ),
    // tooltip: "No",
  },
];

export const DefaultChatBot = Template.bind({});
DefaultChatBot.args = {
  // assistantId: ASSISTANT_IDS.SUPER_AGENT, // page (component) editing bot
  assistantId: ASSISTANT_IDS.PAGE, // page builder bot
  // threadId: "6f44fa48-60bf-4a15-934e-bb94137e0c81",
  apiUrl: LANGGRAPH_DOMAIN,
  title: "What can I help you launch today?",
  subtitle:
    "Generate landing pages, sales funnels, copy, research reports, and much more!",
  suggestions: [],
  placeholder: "Ask Launch OS a question...",
  artifacts,

  onUpdate: action("onUpdate"),
  onFinish: (...args) => {
    console.log("onFinish state:", args[0]?.values);
    action("onFinish")(...args);
  },
  withFeedbackButtons: true,
  feedbackButtons,
};

export const WithSuggestions = Template.bind({});
WithSuggestions.args = {
  ...DefaultChatBot.args,
  suggestions: [
    {
      label: "Create a landing page for my high ticket coaching program",
      icon: Browsers,
      background: "bg-yellow-500 text-black/30 dark:text-white",
    },
    {
      label: "Build a high converting funnel for my AI / Blockchain start up",
      icon: TreeStructure,
      background: "bg-green-500 text-black/50 dark:text-white",
    },
    {
      label:
        "Help me plan a marketing campaign for my free, 3 day coaching workshop",
      icon: NewspaperClipping,
      background: "bg-cyan-500 text-black/30 dark:text-white",
    },
  ],
};

export const WithToolOutputs = Template.bind({});
WithToolOutputs.args = {
  ...DefaultChatBot.args,
  initialMessages: messagesWithToolCalls as Message[],
};

export const WithPageContent = Template.bind({});
WithPageContent.args = {
  ...DefaultChatBot.args,
  assistantId: "d137e314-03bc-5bf8-afa9-6038bfce33e6",
  initialMessages: messagesWithPageContent as Message[],
};

export const CopyWritingArtifact = Template.bind({});
CopyWritingArtifact.args = {
  ...DefaultChatBot.args,
  assistantId: ASSISTANT_IDS.COPY,
  // threadId: "319bb91d-e567-4b4b-a2b0-c4f0411fed85",
  title: "What can I help you write today?",
  subtitle: "Generate copy for your landing pages, sales funnels, and more!",
  suggestions: [],
  placeholder: "Ask the copywriting agent a question...",
  artifacts,
};

export const LaunchOSSuperAgent = Template.bind({});
LaunchOSSuperAgent.args = {
  ...DefaultChatBot.args,
  assistantId: ASSISTANT_IDS.SUPER_AGENT,
  title: "What can I help you launch today?",
  subtitle:
    "Generate landing pages, sales funnels, copy, research reports, and much more!",
  suggestions: [],
  placeholder: "Ask Launch OS a question...",
  artifacts,
  onUpdate: action("onUpdate"),
  onFinish: (...args) => {
    console.log("onFinish state:", args[0]?.values);
    action("onFinish")(...args);
  },
};
